import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { FilterValue, MetricComponent, MetricComponentModel, MetricValue } from 'src/shared/models/pipeline-config/metric-definition.models';
import { ComparisonType, MetricTypes } from 'src/shared/models/pipeline-config/mongo-enums.model';
import { DataFieldDefinition, ReportingSetDefinition, ReportingSetFieldType } from 'src/shared/models/pipeline-config/reporting-sets.models';
import { CalculationCondition } from 'src/shared/services/pipeline-configuration-service-base';
import PipelineConfigurationService from '../pipeline-configuration.service';

@Component({
    selector: 'metric-component-modal',
    templateUrl: './metric-component-modal.component.html',
    styleUrls: ['./metric-component-modal.component.scss']
})
export class MetricComponentModalComponent implements OnInit {

    @Input()
    component: MetricComponent;

    @Input()
    metricType: MetricTypes;

    model: MetricComponentModel = new MetricComponentModel();

    reportingSets: ReportingSetDefinition[] = [];
    reportingSetExists: boolean = true;
    isLoading: boolean = true;

    // Filter Fields only make sense with the reporting set they're associated with.
    // Instead of permanently forgetting them, store them here, in case the user switches away from a reporting set, but switches back.
    private previousReportingSetInfo = new Map<string, PreviousReportingSetInfo>();

    private readonly noDistinctValues: string[] = [];

    constructor(
        private activeModal: NgbActiveModal,
        private cvLocalizationService: CVLocalizationService,
        public service: PipelineConfigurationService, // public, this is used in the markup for the comparison operator symbols
    ) {
    }

    /**
     * Set up the internal state based on the passed-in Component values from an existing component, if there was one.
     */
    async ngOnInit() {
        try {
            await this.cvLocalizationService.GetSection("Pipeline_Configuration");
            this.service.localizeComparisonTypes();
        } catch {}

        try {
            this.reportingSets = await this.service.getReportingSets();
            this.isLoading = false;
        } catch (ex) {
            console.error("Could not load reporting sets");
        }

        this.model = new MetricComponentModel(this.component);

        let previousReportingSetName = this.component?.ReportingSetName;
        if (previousReportingSetName) {
            let reportingSet = this.reportingSets.find(rs => rs.ReportingSetName == previousReportingSetName);
            if (reportingSet) {
                this.model.reportingSet = reportingSet;
                this.model.filterFields.forEach(ff => {
                    this.loadDistinctValuesForFilterField(ff);

                    // Convert Filter Fields for dates from the single string into an array of [date, time] strings.
                    let expectedType = this.getExpectedTypeForFilterField(ff);
                    if (expectedType == ReportingSetFieldType.DateTime) {
                        ff.CompValues = ff.CompValues.map((compValue: string) => {
                            let dateTimeSplit = compValue.split("T");
                            let date = dateTimeSplit[0] + "T00:00:00"; // calling the Date() constructor with just the date string does timezone conversion and at MST it shifts the date back one day.
                            let time = compValue;
                            if (time.endsWith("Z"))
                                time = time.slice(0, -1);
                            return [new Date(date), new Date(time)];
                        });
                    }
                });
                let previousInfo: PreviousReportingSetInfo = { metricDate: this.model.metricDate, filterFields: this.model.filterFields, metricValue: this.model.metricValue, dataValue: this.model.dataValue, endDate: this.model.endDate };
                this.previousReportingSetInfo.set(previousReportingSetName, previousInfo);
            } else {
                this.reportingSetExists = false;
                this.model.filterFields = [];
            }
        }

        if (this.metricType in MetricTypes) {
            this.model.metricType = this.metricType;
        }
    }

    /**
     * Closes the modal without saving the changes.
     * Called by the ngClick directive on the Cancel and "X" buttons.
     */
    cancelClicked() {
        this.activeModal.dismiss();
    }

    /**
     * Returns the data to the parent Edit Metric Component after validating user input.
     * Called by ngClick directive on the Save button.
     */
    saveClicked() {
        let isValid = this.isValid(true);
        if (!isValid)
            return;

        // Convert FilterFields:
        let filterFields: FilterValue[] = this.model.filterFields.map(ff => {
            let copy = {...ff};
            // Set the Negated fields to the right value.  False for everything except for ComparisonType.NotEqual.
            copy.Negated = ff.ComparisonType == ComparisonType.NotEqual;

            // Convert CompValues for Dates into a single string (instead of an array of [date: string, time: Date]):
            let expectedType = this.getExpectedTypeForFilterField(copy);
            if (expectedType == ReportingSetFieldType.DateTime) {
                copy.CompValues = copy.CompValues.map(([date, time]: [Date, Date]) => moment(date).format("YYYY-MM-DD") + (time == null ? 'T00:00:00' : moment(time).format("THH:mm:00")));
                copy.IsDate = true;
            } else {
                delete copy.IsDate;
            }

            return copy;
        });


        let metricComponent: MetricComponent = {
            AttributeFields: this.model.attributeFields,
            DataValue: this.model.dataValue,
            EndDate: this.model.endDate,
            FilterFields: filterFields,
            IsCount: this.model.metricValue == MetricValue.Count,
            IsPositive: this.model.calculationCondition == CalculationCondition.Add,
            IsTime: this.model.metricValue == MetricValue.Time,
            ReportingSetGroup: this.model.reportingSet.ReportingSetGroup,
            ReportingSetName: this.model.reportingSet.ReportingSetName,
            StartDate: this.model.metricDate,
        };

        this.activeModal.close(metricComponent);
    }

    /**
     * Adds a new object to the Filter Field array
     * Called by ngClick directive on the "+ New Filter Field" button
     */
    addNewFilterFieldClicked() {
        this.model.filterFields.push({
            ComparisonType: ComparisonType.Equal,
            CompValues: [null],
            EvalField: null,
            Negated: false,
        });
    }

    /**
     * Removes the filter field at the specified index.
     * Called by ngClick directive on the Delete icon buttons on the far-right of the Filter Field
     * @param index Index of the filter field array to remove
     */
    removeFilterField(index: number) {
        this.model.filterFields.splice(index, 1);
    }

    /**
     * Returns the ExpectedType value from the Reporting Set's DataFields matching the selected "Data Field"
     * Called in expressions in the template
     * @param filterField Filter field to work on
     */
    getExpectedTypeForFilterField(filterField: FilterValue): ReportingSetFieldType {
        let evalField = filterField?.EvalField;
        if (!evalField || evalField.length < 1)
            return null;

        let dataField = this.model.reportingSet?.DataFields?.[evalField];
        if (dataField)
            return dataField.ExpectedType;
        return null;
    }

    /**
     * Checks that the model is valid.
     * Disables the Save button in the template when false.  Also returns false when the Modal is still loading its data.
     */
    isValid(printDebug: boolean = false): boolean {
        let isValid = !this.isLoading;
        // If a reporting set was passed in, but it wasn't found, don't let the user save the component.
        isValid = isValid && this.reportingSetExists;
        isValid = isValid && this.model.reportingSet != null;

        isValid = isValid && this.model.filterFields.every(ff => {
            if (!ff || typeof (ff.EvalField) !== "string" || ff.EvalField.length == 0) {
                printDebug && console.log("invalid FilterField", ff, "EvalField is empty");
                return false;
            }

            if (!this.model.reportingSet?.DataFields?.hasOwnProperty(ff.EvalField)) {
                printDebug && console.log("invalid FilterField", ff, "EvalField isn't in Reporting Set");
                return false;
            }

            let expectedType = this.model.reportingSet.DataFields[ff.EvalField].ExpectedType
            if (expectedType == ReportingSetFieldType.Boolean) {
                if (ff.CompValues.length != 1 || typeof ff.CompValues[0] !== "boolean") {
                    printDebug && console.log("invalid FilterField", ff, "EvalField is of type boolean, but comp value wasn't valid");
                    return false;
                }
            } else if (expectedType == ReportingSetFieldType.String) {
                let allStrings = ff.CompValues.every(cv => typeof cv === "string");
                if (!allStrings) {
                    printDebug && console.log("invalid FilterField", ff, "CompValues aren't all strings");
                    return false;
                }
            } else if (expectedType == ReportingSetFieldType.Int || expectedType == ReportingSetFieldType.Double) {
                let allNumbers = ff.CompValues.every(cv => typeof cv === "number");
                if (!allNumbers) {
                    printDebug && console.log("invalid FilterField", ff, "CompValues aren't all numbers");
                    return false;
                }
            } else if (expectedType == ReportingSetFieldType.DateTime) {
                let allDatesAreGood = ff.CompValues.every(cv => {
                    return Array.isArray(cv) && cv.length == 2 && typeof cv[0] === "object" &&
                        (cv[1] == null || typeof cv[1] === "object");
                });
                if (!allDatesAreGood) {
                    printDebug && console.log("invalid FilterField", ff, "CompValues for date aren't valid");
                    return false;
                }
            }

            return true;
        });

        // This component can't be for a Snapshot/Realtime metric *and* have a component of time:
        isValid = isValid && !(this.model.metricType == MetricTypes.RealTime && this.model.metricValue == MetricValue.Time);

        if (this.model.metricType != MetricTypes.RealTime)
            isValid = isValid && (this.model.metricDate && this.model.reportingSet?.Dates.hasOwnProperty(this.model.metricDate));
        if (this.model.metricValue == MetricValue.DataField) {
            isValid = isValid && (this.model.dataValue && this.model.reportingSet?.DataFields.hasOwnProperty(this.model.dataValue));
        } else if (this.model.metricValue == MetricValue.Time) {
            isValid = isValid && (this.model.endDate && this.model.reportingSet?.Dates.hasOwnProperty(this.model.endDate));
        }

        return isValid;
    }

    /**
     * Put back the previous filter fields, if the changed reporting set already had filter fields.
     */
    reportingSetChanged($event: ReportingSetDefinition) {
        let previousInfo: PreviousReportingSetInfo = {
            metricValue: this.model.metricValue,
            metricDate: this.model.metricDate,
            endDate: this.model.endDate,
            dataValue: this.model.dataValue,
            filterFields: this.model.filterFields,
        };

        if (this.model.reportingSet && this.model.reportingSet != $event)
            this.previousReportingSetInfo.set(this.model.reportingSet.ReportingSetName, previousInfo);

        this.reportingSetExists = !!$event;
        if (this.reportingSetExists) {
            let reportingSetName = $event.ReportingSetName;

            if (this.previousReportingSetInfo.has(reportingSetName)) {
                previousInfo = this.previousReportingSetInfo.get(reportingSetName);
                this.model.metricValue = previousInfo.metricValue;
                this.model.metricDate = previousInfo.metricDate;
                this.model.endDate = previousInfo.endDate;
                this.model.dataValue = previousInfo.dataValue;
                this.model.filterFields = previousInfo.filterFields;
            } else {
                this.model.metricValue = 0;
                this.model.metricDate = undefined;
                this.model.endDate = undefined;
                this.model.dataValue = undefined;
                this.model.filterFields = [];
            }
        } else {
            this.model.metricValue = 0;
            this.model.metricDate = undefined;
            this.model.endDate = undefined;
            this.model.dataValue = undefined;
            this.model.filterFields = [];
        }

        this.model.reportingSet = $event;
    }

    /**
     * Update the distinct values for each filter field.  The endpoint and the call use the metricDate value in the reporting set data pull.
     * Called by ngChange in the Template
     */
    metricDateChanged($event: string) {
        this.model.metricDate = $event;
        this.model.filterFields.forEach(ff => this.loadDistinctValuesForFilterField(ff));
    }

    /**
     * Resets CompValues and loads the distinct values for the new EvalField value of the reporting set.
     * Called by ngChange on template
     * @param filterField 
     */
    filterFieldChanged($event: string, filterField: FilterValue) {
        filterField.EvalField = $event;
        // When the filterField changes, reset Comp Values:
        filterField.CompValues = [];

        this.addCompValue(filterField);

        filterField.ComparisonType = ComparisonType.Equal;
        this.loadDistinctValuesForFilterField(filterField);
    }

    /**
     * Returns the string to display in the Filter Field select.  Appends the field's data type in parentheses.
     * @param fieldName Data Field Name
     * @param fieldInfo
     */
    filterfieldSelectDisplayString(fieldName: string, fieldInfo: DataFieldDefinition): string {
        if (!fieldName || fieldInfo?.ExpectedType == null) {
            return null;
        }

        let displayString = fieldName;
        switch (fieldInfo.ExpectedType) {
            case ReportingSetFieldType.Boolean:
                displayString += ` (${this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Boolean", "Boolean")})`;
                break;
            case ReportingSetFieldType.DateTime:
                displayString += ` (${this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Date", "Date")})`;
                break;
            case ReportingSetFieldType.Double:
                displayString += ` (${this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Floating_Point", "Floating-point")})`;
                break;
            case ReportingSetFieldType.Int:
                displayString += ` (${this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Integer", "Integer")})`;
                break;
            case ReportingSetFieldType.String:
                displayString += ` (${this.cvLocalizationService.getCachedText("Pipeline_Configuration", "String", "String")})`;
                break;
        }

        return displayString;
    }

    /**
     * For string filter fields, load the distinct values for the EvalField in the Reporting Set.  Distinct values are used to fill the cv-combo-box.
     * @param filterField Filter field to load distinct values for.
     */
    private async loadDistinctValuesForFilterField(filterField: FilterValue) {
        let expectedType = this.getExpectedTypeForFilterField(filterField);
        if (expectedType != ReportingSetFieldType.String)
            return;

        await this.service.getDistinctValuesForDataField(this.model.reportingSet.ReportingSetName, filterField.EvalField, this.model.metricDate, 1000);
    }

    /**
     * Returns distinct values loaded by loadDistinctValuesForFilterField().
     * Used in the Template.
     * @param filterField 
     */
    distinctValuesForFilterField(filterField: FilterValue) {
        let distinctValues = this.service.getCachedDistinctValues(this.model.reportingSet.ReportingSetName, filterField.EvalField, this.model.metricDate);
        if (distinctValues.length > 0)
            return distinctValues;
        else
            return this.noDistinctValues;
    }

    /**
     * Adds another CompValue to the given filter field.
     * @param filterField 
     */
    addCompValue(filterField: FilterValue) {
        let expectedType = this.getExpectedTypeForFilterField(filterField);
        if (expectedType == ReportingSetFieldType.Boolean) {
            filterField.CompValues.push(true);
        } else if (expectedType == ReportingSetFieldType.String) {
            filterField.CompValues.push("");
        } else if (expectedType == ReportingSetFieldType.Int || expectedType == ReportingSetFieldType.Double) {
            filterField.CompValues.push(0);
        } else if (expectedType == ReportingSetFieldType.DateTime) {
            // If it's the first CompValue, the initial value will be Now.
            // If it's not the first, copy the last one for better usability.
            if (filterField.CompValues.length > 0) {
                let last = filterField.CompValues[filterField.CompValues.length - 1];
                filterField.CompValues.push([last[0], last[1] == null ? null : new Date(last[1])]);
            } else {
                let now = moment().seconds(0);
                filterField.CompValues.push([now.toDate(), now.toDate()]); // The two values will be combined later. The date part of time object will be ignored.
            }
        }
    }

    /**
     * Removes the CompValue at the given index, if CompValues has more than one element.
     * @param filterField Filter field to remove from
     * @param index Index value of the CompValues array
     */
    removeCompValue(filterField: FilterValue, index: number) {
        if (filterField.CompValues.length > 1) {
            filterField.CompValues.splice(index, 1);
        }
    }

    /**
     * Utility function to track FilterFields by index
     * @param index Index of the array element used in ngFor
     */
    trackByIndex(index: number) {
        return index;
    }
}

export default MetricComponentModalComponent;

type PreviousReportingSetInfo = { metricValue: number, metricDate: string, dataValue: string, endDate: string, filterFields: FilterValue[] };
